import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { metaAdsLivePreviewDraftReady } from "@/components/pages/metaAds/metaAdsUtils";
import {
  fetchMetaAdsAdPreviewLive,
  isMetaAdsApiConfigured,
} from "./useMetaAdsApi";

function serializeDraft(draft) {
  if (!draft || typeof draft !== "object") {
    return "";
  }
  return JSON.stringify({
    destinationUrl: String(draft.destinationUrl ?? "").trim(),
    imageUrl: String(draft.imageUrl ?? "").trim(),
    primaryText: String(draft.primaryText ?? "").trim(),
    pageId: String(draft.pageId ?? "").trim(),
    campaignObjective: String(draft.campaignObjective ?? "").trim(),
  });
}

/**
 * Debounced Marketing API ``generatepreviews`` for unsaved link creatives (mobile feed).
 */
export function useMetaAdsAdLivePreview(adId, draft, options = {}) {
  const {
    enabled = true,
    debounceMs = 450,
    adFormat = "MOBILE_FEED_STANDARD",
  } = options;
  const id = String(adId || "").trim();
  const draftKey = useMemo(
    () => serializeDraft(draft),
    [
      draft?.destinationUrl,
      draft?.imageUrl,
      draft?.primaryText,
      draft?.pageId,
      draft?.campaignObjective,
    ],
  );

  const [debouncedKey, setDebouncedKey] = useState(draftKey);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKey(draftKey), debounceMs);
    return () => clearTimeout(t);
  }, [draftKey, debounceMs]);

  const stableDraft = useMemo(() => {
    try {
      return debouncedKey ? JSON.parse(debouncedKey) : {};
    } catch {
      return {};
    }
  }, [debouncedKey]);

  const debouncedDraftReady = useMemo(
    () => metaAdsLivePreviewDraftReady(stableDraft),
    [stableDraft],
  );

  const format =
    String(adFormat || "MOBILE_FEED_STANDARD")
      .trim()
      .toUpperCase() || "MOBILE_FEED_STANDARD";

  return useQuery({
    queryKey: ["meta-ads-ad-preview-live", id, format, debouncedKey],
    queryFn: () =>
      fetchMetaAdsAdPreviewLive(id, {
        destinationUrl: stableDraft.destinationUrl,
        imageUrl: stableDraft.imageUrl,
        primaryText: stableDraft.primaryText,
        pageId: stableDraft.pageId,
        campaignObjective: stableDraft.campaignObjective || undefined,
        adFormat: format,
      }),
    enabled: Boolean(
      enabled &&
      id &&
      debouncedKey &&
      debouncedDraftReady &&
      isMetaAdsApiConfigured(),
    ),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
